import { Fragment, useContext, useMemo } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"
import { isEmpty as _isEmpty } from "lodash"

import { InfoBlock } from "../../../components/containers/InfoBlock"
import { BasePieChart } from "../../../components/graphs/PieChart"
import { KonanAPI } from "../../../services/KonanAPI"
import { InsightList } from "../../../types/generated/api/InsightList"
import { ReportContext } from "../Context"
import { generateLabelInsightChartData, generateLabelInsightsSummaryChartData } from "../helpers"
import type { InsightProps, LabelInsightProps } from "../interfaces"

/**
 * LabelInsight Component
 *
 * Renders a visualization of label insights, displaying data points and their success/failure breakdown.
 * Fetches insight data from the API and processes it for chart visualization.
 *
 * @param {InsightProps} props - The properties for the component, including:
 *   - insight: Insight metadata containing:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 *   - isFeedback: Boolean indicating if the insight is related to feedback
 *   - reportType: Type of the report (e.g., simulation)
 *   - isFeedbackColumn: Boolean indicating if the feedback column is displayed
 * @returns {React.ReactElement} A React fragment containing InfoBlocks and a BasePieChart visualization
 */

export const LabelInsight = (props: InsightProps): React.ReactElement => {
  const { insight, isFeedback, reportType, isFeedbackColumn } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { report } = useContext(ReportContext)

  const { t } = useTranslation("insights")

  // Fetch labels insight data from the API
  const { data: labelInsight, isLoading: isLoadingLabelInsight } = useQuery<
    AxiosResponse<LabelInsightProps>,
    AxiosError
  >(
    ["report", projectId, report?.uuid, insight.uuid],
    () =>
      KonanAPI.fetchInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
      }),
    {
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  // Process the fetched data to create chart data for the pie chart visualization
  const chartData = useMemo(
    () =>
      generateLabelInsightChartData({
        data: labelInsight?.data,
        isFeedback: isFeedback,
        isLoading: isLoadingLabelInsight,
      }),
    [isFeedback, isLoadingLabelInsight, labelInsight?.data],
  )

  const isEmpty = _isEmpty(chartData) // Check if the pie chart data is empty

  // Process the fetched data to create summary chart data with success/failure breakdown
  const SummaryChartData = useMemo(
    () =>
      generateLabelInsightsSummaryChartData({
        data: labelInsight?.data,
        isFeedback: isFeedback,
        isLoading: isLoadingLabelInsight,
      }),
    [isFeedback, isLoadingLabelInsight, labelInsight?.data],
  )

  // Render the BasePieChart component
  return (
    <Fragment>
      {(reportType === "simulation" && isFeedback) || (
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12} lg={4}>
            <InfoBlock
              type="large"
              title={
                isFeedback
                  ? t("LabelInsight.infoBlocks.expectedFeedback.title")
                  : t("LabelInsight.infoBlocks.totalDataPoints.title")
              }
              text={labelInsight?.data?.count ?? 0}
              isLoading={isLoadingLabelInsight}
              titleTooltip={
                isFeedback
                  ? t("LabelInsight.infoBlocks.expectedFeedback.tooltip")
                  : t("LabelInsight.infoBlocks.totalDataPoints.tooltip")
              }
              textTransform="initial"
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <InfoBlock
              type="large"
              title={
                isFeedback
                  ? t("LabelInsight.infoBlocks.receivedFeedback.title")
                  : t("LabelInsight.infoBlocks.successfulDataPoints.title")
              }
              text={SummaryChartData["Success"]?.value ?? 0}
              isLoading={isLoadingLabelInsight}
              titleTooltip={
                isFeedback
                  ? t("LabelInsight.infoBlocks.receivedFeedback.tooltip")
                  : t("LabelInsight.infoBlocks.successfulDataPoints.tooltip")
              }
              textTransform="initial"
              tagProps={{
                text: `${(((SummaryChartData["Success"]?.value ?? 0) / (labelInsight?.data?.count ?? 0)) * 100).toFixed(2)}%`,
                variant: "green",
              }}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <InfoBlock
              title={
                isFeedback
                  ? t("LabelInsight.infoBlocks.missingFeedback.title")
                  : t("LabelInsight.infoBlocks.failedDataPoints.title")
              }
              text={SummaryChartData["Failed"]?.value ?? 0}
              isLoading={isLoadingLabelInsight}
              titleTooltip={
                isFeedback
                  ? t("LabelInsight.infoBlocks.missingFeedback.tooltip")
                  : t("LabelInsight.infoBlocks.failedDataPoints.tooltip")
              }
              textTransform="initial"
              type="large"
              tagProps={{
                text: `${(((SummaryChartData["Failed"]?.value ?? 0) / (labelInsight?.data?.count ?? 0)) * 100).toFixed(2)}%`,
                variant: "yellow",
              }}
            />
          </Grid>
        </Grid>
      )}

      <Grid
        item
        xs={reportType === "simulation" && isFeedbackColumn ? 6 : 12}
        lg={reportType === "simulation" && isFeedbackColumn ? 0 : 4}
      >
        <BasePieChart
          reportType={reportType}
          title={isFeedback ? t("LabelInsight.pieChart.title.feedback") : t("LabelInsight.pieChart.title.decisions")}
          tooltipText={
            <Trans>
              {t("LabelInsight.pieChart.tooltipText", {
                dataType: isFeedback
                  ? t("LabelInsight.pieChart.label.feedback")
                  : t("LabelInsight.pieChart.label.decisions"),
                categoryType: isFeedback
                  ? t("LabelInsight.pieChart.label.feedback")
                  : t("LabelInsight.pieChart.label.outcome"),
              })}
            </Trans>
          }
          isLoading={isLoadingLabelInsight}
          data={chartData}
          graphHeight={reportType === "simulation" ? 188 : 150}
          label={{
            name: t("label.total"),
            value: labelInsight?.data.count ?? 0,
          }}
          orientation="vertical"
          emptyState={
            insight.status !== InsightList.status.SUCCEEDED
              ? {
                  title: `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
                    defaultValue: insight.status,
                  })?.toLocaleLowerCase()}`,
                }
              : isEmpty
                ? {
                    title: t(
                      isFeedback
                        ? "LabelInsight.pieChart.unavailable.feedback"
                        : "LabelInsight.pieChart.unavailable.decisions",
                    ),
                  }
                : undefined
          }
        />
      </Grid>
    </Fragment>
  )
}
